import { isWithinInterval, format, isBefore, parseISO, subDays, isToday } from 'date-fns';
import { attendanceAlertType } from './AttendanceAlertModal';

type ConfirmedSession = {
  classId: number;
  sessionDate: string; // yyyy-MM-dd
  startTime?: string;  // HH:MM
  endTime?: string;    // HH:MM
  isActive: boolean;
  confirmedAt?: string | null;
};

type Attendance = {
  id: number;
  classId: number;
  date: string | Date; // could be ISO string or Date
};

type Class = {
  id: number;
  name: string;
  teacherId: number;
};

type Teacher = {
  id: number;
  userName: string;
};

const DAY_NAMES: Record<number, string> = {
  2: 'Thứ Hai', 3: 'Thứ Ba', 4: 'Thứ Tư',
  5: 'Thứ Năm', 6: 'Thứ Sáu', 7: 'Thứ Bảy', 8: 'Chủ Nhật',
};

/** Parse "HH:MM" into total minutes since midnight */
const parseTimeToMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

export const checkMissingAttendance = (
  confirmedSessions: ConfirmedSession[],
  attendanceSessions: Attendance[],
  classes: Class[],
  teachers: Teacher[]
): {
  teacherName: string;
  messages: { text: string; classId: number; className: string; rawDate: string; dayOfWeek: number }[];
  attendanceAlertType: string;
}[] => {
  const today = new Date();
  const thirtyDaysAgo = subDays(today, 30);
  const nowMinutes = today.getHours() * 60 + today.getMinutes();

  // Pre-build a Set of "classId_yyyy-MM-dd" for O(1) attendance lookup
  const attendanceSet = new Set<string>(
    attendanceSessions.map(att => {
      const dateStr = typeof att.date === 'string'
        ? att.date.slice(0, 10)          // handles "2026-05-01T00:00:00Z" or "2026-05-01"
        : format(att.date as Date, 'yyyy-MM-dd');
      return `${att.classId}_${dateStr}`;
    })
  );

  const alerts: Record<number, { text: string; classId: number; className: string; rawDate: string; dayOfWeek: number }[]> = {};

  confirmedSessions.forEach(session => {
    // Only active confirmed sessions
    if (!session.isActive || !session.confirmedAt) return;

    const sessionDate = parseISO(session.sessionDate);

    // Only sessions within the past 30 days and strictly before now
    if (!isWithinInterval(sessionDate, { start: thirtyDaysAgo, end: today })) return;
    if (!isBefore(sessionDate, today)) return;

    // ── Nếu buổi học là HÔM NAY: chỉ nhắc khi đã qua giữa buổi ──
    if (isToday(sessionDate)) {
      if (session.startTime && session.endTime) {
        const start = parseTimeToMinutes(session.startTime);
        const end   = parseTimeToMinutes(session.endTime);
        const mid   = start + Math.floor((end - start) / 2);
        if (nowMinutes < mid) return; // chưa tới giữa buổi → bỏ qua
      } else {
        // Không có thông tin giờ → không nhắc buổi hôm nay để tránh nhắc quá sớm
        return;
      }
    }

    // Check attendance exists
    const key = `${session.classId}_${session.sessionDate}`;
    if (attendanceSet.has(key)) return;

    // Find class info
    const classItem = classes.find(c => c.id === session.classId);
    if (!classItem) return;

    // Compute day-of-week label (DB convention: 2=Thứ Hai … 8=CN)
    const jsDay = sessionDate.getDay();   // 0=Sun, 1=Mon, ..., 6=Sat
    const dayOfWeek = jsDay === 0 ? 8 : jsDay + 1;
    const dayLabel = DAY_NAMES[dayOfWeek] || '';

    const displayDate = format(sessionDate, 'dd/MM/yyyy');
    const timeLabel = session.startTime && session.endTime
      ? ` (${session.startTime}–${session.endTime})`
      : '';

    if (!alerts[classItem.teacherId]) alerts[classItem.teacherId] = [];
    alerts[classItem.teacherId].push({
      text: `Lớp ${classItem.name}, ${dayLabel} ngày ${displayDate}${timeLabel}`,
      classId: session.classId,
      className: classItem.name,
      rawDate: session.sessionDate,
      dayOfWeek,
    });
  });

  return Object.entries(alerts).map(([teacherId, messages]) => {
    const teacher = teachers.find(t => t.id === parseInt(teacherId, 10));
    return {
      teacherName: teacher?.userName || 'Unknown',
      messages,
      attendanceAlertType: attendanceAlertType.MISSING,
    };
  });
};
